<template>
  <div class="configurable-dashboard">
    <div class="dashboard-toolbar">
      <template v-if="!editMode">
        <md-button v-if="canEdit" class="md-icon-button md-dense md-simple dashboard-edit-trigger" :title="$t('dashboard.edit')" @click="startEdit">
          <md-icon>edit</md-icon>
        </md-button>
        <span v-if="isCustom" class="dashboard-hint">{{ $t("dashboard.customActive") }}</span>
      </template>
      <template v-else>
        <md-button class="md-success md-sm" @click="addPickerOpen = true"><md-icon>add</md-icon> {{ $t("dashboard.addWidget") }}</md-button>
        <md-button class="md-primary md-sm" @click="saveUser"><md-icon>save</md-icon> {{ $t("dashboard.save") }}</md-button>
        <md-button v-if="isAdminUser" class="md-warning md-sm" @click="saveMaster"><md-icon>public</md-icon> {{ $t("dashboard.saveMaster") }}</md-button>
        <md-button class="md-simple md-sm" @click="resetToMaster"><md-icon>restart_alt</md-icon> {{ $t("dashboard.reset") }}</md-button>
        <md-button class="md-simple md-sm" @click="cancelEdit">{{ $t("dashboard.done") }}</md-button>
      </template>
    </div>

    <div v-if="isLoading" class="dashboard-loading">{{ $t("dashboard.loading") }}</div>
    <div v-else-if="!layout.length" class="dashboard-empty">
      {{ $t("dashboard.empty") }}
      <div v-if="!editMode && canEdit"><md-button class="md-primary md-sm" @click="startEdit">{{ $t("dashboard.edit") }}</md-button></div>
    </div>

    <grid-layout
      v-else
      :layout.sync="layout"
      :col-num="12"
      :row-height="30"
      :is-draggable="editMode"
      :is-resizable="editMode"
      :margin="[12, 12]"
      :use-css-transforms="true"
      :vertical-compact="true"
    >
      <grid-item
        v-for="item in layout"
        :key="item.i"
        :x="item.x"
        :y="item.y"
        :w="item.w"
        :h="item.h"
        :i="item.i"
        :min-w="minW(item)"
        :min-h="minH(item)"
        drag-allow-from=".widget-drag-handle"
        class="dashboard-grid-item"
        :class="{ 'is-editing': editMode }"
      >
        <div class="widget-frame">
          <div v-if="editMode" class="widget-edit-bar">
            <span class="widget-drag-handle"><md-icon>drag_indicator</md-icon></span>
            <md-button class="md-icon-button md-dense md-simple widget-remove" @click="removeWidget(item)"><md-icon>close</md-icon></md-button>
          </div>
          <component v-if="registry[item.widget] && canUseWidget(item.widget)" :is="registry[item.widget].component" />
          <div v-else-if="!registry[item.widget]" class="widget-unknown">{{ $t("dashboard.unknownWidget", { type: item.widget }) }}</div>
          <div v-else class="widget-unknown">{{ $t("dashboard.noWidgetPermission") }}</div>
        </div>
      </grid-item>
    </grid-layout>

    <!-- Widget-Picker -->
    <modal v-if="addPickerOpen" @close="addPickerOpen = false" class-name="widget-picker-modal">
      <template slot="header">
        <h4 class="modal-title">{{ $t("dashboard.pickWidget") }}</h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="addPickerOpen = false"><md-icon>clear</md-icon></md-button>
      </template>
      <template slot="body">
        <div class="widget-picker-list">
          <div v-for="(def, type) in availableWidgets" :key="type" class="widget-picker-item" @click="addWidget(type)">
            <md-icon>{{ def.icon }}</md-icon>
            <span>{{ $t(def.titleKey) }}</span>
          </div>
        </div>
      </template>
    </modal>
  </div>
</template>
<script>
import { GridLayout, GridItem } from "vue-grid-layout";
import { Modal } from "@/components";
import axiosFactory from "../services/axios.factory";
import { widgetRegistry } from "./Widgets/widgetRegistry";
import { isAdmin, hasPermission } from "../mixins/auth";

export default {
  name: "ConfigurableDashboard",
  components: { GridLayout, GridItem, Modal },
  props: {
    // Speicher-Schlüssel des Layouts (z.B. "main", "procurement", "wms").
    dashboardKey: { type: String, required: true },
    // Menü-Bereich für das Bearbeiten-Recht (Write/EditLayout), z.B. "Dashboard".
    permissionKey: { type: String, default: "" },
    // Code-Default, falls weder persönliches noch Master-Layout existiert.
    defaultWidgets: { type: Array, default: () => [] },
  },
  data() {
    return {
      isLoading: true,
      editMode: false,
      isCustom: false,
      hasMaster: false,
      layout: [],
      layoutBeforeEdit: null,
      addPickerOpen: false,
      registry: widgetRegistry,
      counter: 1,
    };
  },
  computed: {
    isAdminUser() {
      return isAdmin();
    },
    // Darf dieser Benutzer das Dashboard bearbeiten? Recht: Write/EditLayout auf dem Menü-Bereich.
    canEdit() {
      if (isAdmin()) return true;
      if (!this.permissionKey) return false;
      return hasPermission("Write", this.permissionKey, "EditLayout");
    },
    // Nur Widgets im Picker, die der Benutzer auch nutzen darf.
    availableWidgets() {
      const out = {};
      Object.keys(widgetRegistry).forEach((type) => {
        if (this.canUseWidget(type)) out[type] = widgetRegistry[type];
      });
      return out;
    },
  },
  methods: {
    canUseWidget(type) {
      const def = widgetRegistry[type];
      if (!def) return false;
      if (!def.permission) return true;
      if (isAdmin()) return true;
      const p = def.permission;
      return hasPermission(p.permissionName, p.tableName, p.columnName);
    },
    resolveWidget(type) {
      if (!this.canUseWidget(type)) return null;
      return (widgetRegistry[type] && widgetRegistry[type].component) || null;
    },
    minW(item) {
      return (widgetRegistry[item.widget] && widgetRegistry[item.widget].minW) || 2;
    },
    minH(item) {
      return (widgetRegistry[item.widget] && widgetRegistry[item.widget].minH) || 4;
    },
    defaultLayout() {
      // Code-Fallback aus den default-widgets des Dashboards, falls weder persönlich noch Master.
      let y = 0;
      return this.defaultWidgets
        .filter((t) => widgetRegistry[t])
        .map((t, idx) => {
          const def = widgetRegistry[t];
          const item = { i: "w" + (idx + 1), widget: t, x: 0, y, w: def.defaultW, h: def.defaultH };
          y += def.defaultH;
          return item;
        });
    },
    nextId() {
      let id;
      do {
        id = "w" + this.counter++;
      } while (this.layout.some((l) => l.i === id));
      return id;
    },
    parseLayout(json) {
      try {
        const arr = JSON.parse(json);
        if (Array.isArray(arr) && arr.length) return arr.map((l) => ({ ...l }));
      } catch (e) {
        /* ignore */
      }
      return null;
    },
    async load() {
      try {
        const res = await axiosFactory().get(`/reporting/dashboard_layout/${this.dashboardKey}`);
        const data = res.data.data || {};
        this.isCustom = !!data.isCustom;
        this.hasMaster = !!data.hasMaster;
        this.layout = this.parseLayout(data.layoutJson) || this.defaultLayout();
      } catch (e) {
        this.layout = this.defaultLayout();
      } finally {
        this.isLoading = false;
      }
    },
    startEdit() {
      this.layoutBeforeEdit = JSON.stringify(this.layout);
      this.editMode = true;
    },
    cancelEdit() {
      this.editMode = false;
      this.addPickerOpen = false;
    },
    addWidget(type) {
      const def = widgetRegistry[type];
      if (!def) return;
      const maxY = this.layout.reduce((m, l) => Math.max(m, l.y + l.h), 0);
      this.layout.push({ i: this.nextId(), widget: type, x: 0, y: maxY, w: def.defaultW, h: def.defaultH });
      this.addPickerOpen = false;
    },
    removeWidget(item) {
      this.layout = this.layout.filter((l) => l.i !== item.i);
    },
    serialize() {
      // nur die relevanten Felder speichern
      return JSON.stringify(this.layout.map((l) => ({ i: l.i, widget: l.widget, x: l.x, y: l.y, w: l.w, h: l.h })));
    },
    notify(messageKey, type = "success") {
      this.$notify({ timeout: 2500, message: this.$t(messageKey), icon: "add_alert", horizontalAlign: "right", verticalAlign: "top", type });
    },
    async saveUser() {
      try {
        await axiosFactory().put(`/reporting/dashboard_layout/${this.dashboardKey}`, { layoutJson: this.serialize() });
        this.isCustom = true;
        this.editMode = false;
        this.notify("dashboard.messages.saved");
      } catch (e) {
        this.notify("dashboard.messages.error", "danger");
      }
    },
    async saveMaster() {
      try {
        await axiosFactory().put(`/reporting/dashboard_layout/${this.dashboardKey}/master`, { layoutJson: this.serialize() });
        this.hasMaster = true;
        this.notify("dashboard.messages.masterSaved");
      } catch (e) {
        this.notify("dashboard.messages.error", "danger");
      }
    },
    async resetToMaster() {
      try {
        await axiosFactory().delete(`/reporting/dashboard_layout/${this.dashboardKey}`);
        this.isCustom = false;
        this.editMode = false;
        this.isLoading = true;
        await this.load();
        this.notify("dashboard.messages.reset");
      } catch (e) {
        this.notify("dashboard.messages.error", "danger");
      }
    },
  },
  mounted() {
    this.load();
  },
};
</script>
<style lang="scss">
.configurable-dashboard {
  .dashboard-toolbar {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    min-height: 40px;

    .dashboard-edit-trigger {
      margin: 0;
      .md-icon {
        color: #9e9e9e !important;
      }
      &:hover .md-icon {
        color: #616161 !important;
      }
    }

    .dashboard-hint {
      font-size: 12px;
      color: #888;
    }
  }

  .dashboard-loading,
  .dashboard-empty {
    padding: 40px 8px;
    text-align: center;
    color: #999;
  }

  .dashboard-grid-item {
    touch-action: none;
    overflow: visible;

    &.is-editing {
      .widget-frame {
        outline: 1px dashed #bbb;
        border-radius: 8px;
      }
      .vue-resizable-handle {
        z-index: 3;
      }
    }
  }

  .widget-frame {
    height: 100%;
    position: relative;
    // Platz oben fuer das schwebende Karten-Icon (md-card-header-icon), sonst wird es abgeschnitten.
    padding-top: 16px;
    overflow: visible;

    // Widget-Karten fuellen die Kachel
    .md-card {
      height: 100%;
      margin: 0;
      display: flex;
      flex-direction: column;
    }
    .md-card-content {
      flex: 1;
      overflow: auto;
    }
  }

  .widget-edit-bar {
    position: absolute;
    top: -2px;
    right: 6px;
    z-index: 4;
    display: flex;
    align-items: center;
    gap: 4px;

    .widget-drag-handle {
      cursor: move;
      color: #777;
      display: inline-flex;
      background: rgba(255, 255, 255, 0.85);
      border-radius: 4px;
    }
    .widget-remove {
      margin: 0;
      background: rgba(255, 255, 255, 0.85);
    }
  }

  .widget-unknown {
    padding: 24px;
    text-align: center;
    color: #b71c1c;
  }
}

.widget-picker-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  min-width: 320px;
  padding-top: 16px;

  .widget-picker-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    width: 120px;
    padding: 16px 8px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    cursor: pointer;

    &:hover {
      background: #f5f5f5;
      border-color: #7f915a;
    }
    .md-icon {
      font-size: 32px !important;
    }
  }
}
</style>
